// The interactive diagram surface shared by the model, timeline and compare views: cards, edges,
// search, focus, layouts, property modes and fitting. Callers supply the graph, positions and selection.
import * as Menu from '@radix-ui/react-dropdown-menu';
import {
  Background,
  BackgroundVariant,
  ConnectionMode,
  ControlButton,
  Controls,
  getViewportForBounds,
  MiniMap,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  useNodesState,
  useReactFlow,
  useStore,
  type Connection,
  type Node,
  type NodeProps,
  type OnNodeDrag,
  type Viewport,
} from '@xyflow/react';
import { deleteImpact, guessDatabase, type DeleteDatabase, type ModelSpec } from '@modelviewer/core';
import { ArrowDownFromLine, ArrowLeft, ArrowLeftFromLine, ArrowRightFromLine, ArrowUpFromLine, Boxes, ChevronDown, ChevronRight, CornerDownRight, Crosshair, Hand, Maximize2, Minimize2, Move, MoveHorizontal, Database, Grid3x3, Maximize, Orbit, RotateCcw, Search, Share2, Spline, Waypoints, X } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../lib/cn';
import { INSPECTOR_DOCKS, TOUCH, useMedia } from '../lib/media';
import { showSidebar } from '../state/sidebars';
import { DiagramContext, zoomedOutAt, type DiagramInteraction } from './context';
import { CascadeIcon, DeletePanel } from './DeletePanel';
import { deletionOverlay, pathsOf } from './deletion';
import { EntityNode, type EntityNodeType } from './EntityNode';
import { focusArrangement, focusGraph, type FocusDepth, type FocusGraph } from './focus';
import type { EntityNodeData, Graph, GraphEdge, PropertyMode, RowData } from './graph';
import { autoLayout } from './layout';
import { assignGroups, gridLayout, groupLabels, isFlow, isGrouped, LAYOUTS, radialLayout, SPACINGS, spacingScale, type LayoutKind, type Positions, type Spacing } from './layouts';
import { DiagramMarkers } from './Markers';
import { measureText } from './measure';
import { RelationshipEdge, type DiagramEdgeType } from './RelationshipEdge';
import { useDiagramSettings, type EdgeStyle } from './settings';
import { layoutInput } from './useLayout';

type FrameNodeType = Node<{ label: string; count: number }, 'frame'>;
type CanvasNode = EntityNodeType | FrameNodeType;

/** The frame around the cards of one namespace or schema (grouped layouts). */
function FrameNode({ data }: NodeProps<FrameNodeType>) {
  return (
    <div className="mv-frame">
      <span className="mv-frame-label">
        {data.label}
        <span className="mv-frame-count">{data.count}</span>
      </span>
    </div>
  );
}

const nodeTypes = { entity: EntityNode, frame: FrameNode };
const edgeTypes = { relationship: RelationshipEdge };

const LAYOUT_ICONS: Record<LayoutKind, React.ReactNode> = {
  right: <ArrowRightFromLine size={14} />,
  left: <ArrowLeftFromLine size={14} />,
  down: <ArrowDownFromLine size={14} />,
  up: <ArrowUpFromLine size={14} />,
  organic: <Share2 size={14} />,
  radial: <Orbit size={14} />,
  grid: <Grid3x3 size={14} />,
  namespace: <Boxes size={14} />,
  schema: <Database size={14} />,
};

export interface CanvasSelection {
  /** Node id. */
  node: string | null;
  /** `${nodeId}::${rowId}` */
  row: string | null;
  /** Edge id. */
  edge: string | null;
}

export type CanvasTarget = { kind: 'node'; node: EntityNodeData } | { kind: 'row'; node: EntityNodeData; row: RowData } | { kind: 'edge'; edge: GraphEdge };

export interface CanvasProps {
  graph: Graph;
  positions: Positions;
  selected: CanvasSelection;
  onSelect: (target: CanvasTarget | null) => void;
  /** Cards were dragged to these positions. */
  onMoved: (update: Positions) => void;
  onRelayout: () => void;
  /** The view fits the diagram whenever this changes. */
  fitKey: string;
  /** Brings a node into view (or focuses on it, or turns cascade paths on for it); a new nonce repeats the request. */
  reveal: { id: string; nonce: number; focus?: boolean; deletion?: boolean } | null;
  /** Top-right summary. */
  status: React.ReactNode;
  /** Fade cards and edges that did not change. */
  dimUnchanged?: boolean;
  /** More toolbar buttons. */
  tools?: React.ReactNode;
  /** Sketches: dragging from a card's handle onto another card calls this (dependent, principal). */
  onConnect?: (from: EntityNodeData, to: EntityNodeData) => void;
  /** Lets the owner place new cards in view. */
  apiRef?: React.RefObject<CanvasApi | null>;
  /** Side by side: follow this viewport, and report when the user pans or zooms. */
  viewport?: Viewport | null;
  onViewport?: (viewport: Viewport) => void;
  /** Hide the toolbar (the second canvas of a side-by-side view). */
  bare?: boolean;
  /** Focus started or ended, so the owner can show every property of the focused card. */
  onFocusChange?: (focused: boolean) => void;
  /** What the diagram calls its cards and rows, and its own row modes (the code map); otherwise the model's. */
  vocabulary?: Vocabulary;
  /** The model the cards show, when node ids are its entity keys: cascade paths can then show what deleting a row does. */
  model?: ModelSpec;
}

export interface Vocabulary {
  /** Placeholder and label of the search box. */
  search: string;
  /** Name of the group of row modes. */
  modesLabel: string;
  modes: { value: PropertyMode; label: string; title?: string }[];
  /** The row mode in use and how to change it (instead of the shared diagram setting). */
  mode: PropertyMode;
  setMode: (mode: PropertyMode) => void;
  /** Offer the button that draws join tables as many-to-many links. */
  joins: boolean;
}

export interface CanvasApi {
  /** The visible area, in diagram coordinates. */
  visible: () => { x: number; y: number; width: number; height: number };
  /** Shows the whole diagram. */
  fitAll: () => void;
}

export function Canvas(props: CanvasProps) {
  return (
    <ReactFlowProvider>
      <CanvasInner {...props} />
    </ReactFlowProvider>
  );
}

function matchesSearch(node: EntityNodeData, query: string): boolean {
  const q = query.toLowerCase();
  if (node.key.toLowerCase().includes(q) || (node.table ?? '').toLowerCase().includes(q)) return true;
  return node.lines.some((l) => (l.kind === 'row' ? l.row.name : l.section.name).toLowerCase().includes(q));
}

const MODES: { value: PropertyMode; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'keys', label: 'Keys' },
  { value: 'none', label: 'Names' },
];

const TWEEN_MS = 320;

const FOCUS_DEPTHS: FocusDepth[] = [1, 2, 3, 'all'];

const SPACING_ICONS: Record<Spacing, React.ReactNode> = {
  compact: <Minimize2 size={13} />,
  normal: <MoveHorizontal size={13} />,
  roomy: <Maximize2 size={13} />,
};

export const MENU_LABEL = 'px-1.5 pt-1 pb-1.5 text-[11px] font-semibold tracking-wider text-faint uppercase';
export const SEGMENT =
  'flex cursor-default items-center justify-center gap-1.5 rounded-md px-1.5 py-1.5 text-[12px] text-muted outline-none data-[highlighted]:bg-active data-[highlighted]:text-text data-[state=checked]:bg-accent-soft data-[state=checked]:text-accent';

const EDGE_STYLES: { id: EdgeStyle; label: string; icon: React.ReactNode }[] = [
  { id: 'square', label: 'Right angles', icon: <CornerDownRight size={13} /> },
  { id: 'smooth', label: 'Smooth curves', icon: <Spline size={13} /> },
];

/** Above this many cards, moving every card and line each frame is too slow: the browser glides them. */
const FRAME_BY_FRAME_CARDS = 80;

/**
 * Positions that glide to each new target (a layout switch, focus) instead of jumping. `settle`
 * records where cards were dropped, so a drag does not glide back from where it started.
 *
 * A small diagram moves its cards frame by frame and the lines follow. A large one would redraw
 * hundreds of cards and lines each frame, so its cards take their new places at once and a CSS
 * transition glides them there, off the main thread, while the lines wait hidden (`gliding`;
 * see `.mv-gliding` in index.css).
 */
function useGlide(target: Positions | null, large: boolean): { shown: Positions | null; gliding: boolean; settle: (update: Positions) => void } {
  const [shown, setShown] = useState<Positions | null>(target);
  const [gliding, setGliding] = useState(false);
  const current = useRef<Positions | null>(target);
  const glideEnd = useRef(0);
  useEffect(() => () => clearTimeout(glideEnd.current), []);
  useEffect(() => {
    if (!target) return;
    const from = current.current;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const moves = !!from && Object.entries(target).some(([id, p]) => from[id] && (Math.abs(from[id].x - p.x) > 1 || Math.abs(from[id].y - p.y) > 1));
    if (!from || reduce || !moves) {
      current.current = target;
      setShown(target);
      return;
    }
    if (large) {
      // The class goes on with the new positions, so the transition applies to them; it comes off
      // a little after the transition ends (the positions reach the cards a frame or two later).
      current.current = target;
      setShown(target);
      setGliding(true);
      clearTimeout(glideEnd.current);
      glideEnd.current = window.setTimeout(() => setGliding(false), TWEEN_MS + 120);
      return;
    }
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / TWEEN_MS);
      const k = 1 - (1 - t) ** 3;
      const next: Positions = {};
      for (const [id, p] of Object.entries(target)) {
        const a = from[id];
        next[id] = a ? { x: a.x + (p.x - a.x) * k, y: a.y + (p.y - a.y) * k } : p;
      }
      current.current = next;
      setShown(next);
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, large]);
  const settle = useCallback((update: Positions) => {
    current.current = { ...current.current, ...update };
  }, []);
  return { shown, gliding, settle };
}

/** Whether the element is narrower than `width` pixels. */
function useNarrow(element: React.RefObject<HTMLElement | null>, width: number): boolean {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const el = element.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => entry && entry.contentRect.width > 0 && setNarrow(entry.contentRect.width < width));
    observer.observe(el);
    return () => observer.disconnect();
  }, [element, width]);
  return narrow;
}

/**
 * Where the cascade paths list starts: level with the toolbar when there is room beside it and the
 * focus bar, otherwise below them (null for level).
 */
function useListTop(container: React.RefObject<HTMLElement | null>, shown: boolean): number | null {
  const [top, setTop] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = container.current;
    const bars = el?.querySelector<HTMLElement>('.react-flow__panel.top.left');
    if (!shown || !el || !bars) return;
    const measure = () => {
      const box = el.getBoundingClientRect();
      const left = bars.getBoundingClientRect();
      // The list is 340px wide and 12px in from the right; it keeps 12px from the bars.
      setTop(left.right + 12 > box.right - 352 ? left.bottom - box.top + 8 : null);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    observer.observe(bars);
    return () => observer.disconnect();
  }, [container, shown]);
  return top;
}

interface Focus {
  id: string;
  /** Where focus came from, to step back. */
  trail: string[];
}

function CanvasInner({ graph, positions, selected, onSelect, onMoved, onRelayout, fitKey, reveal, status, dimUnchanged, tools, onConnect, apiRef, viewport, onViewport, bare, onFocusChange, vocabulary, model }: CanvasProps) {
  const settings = useDiagramSettings();
  const words = vocabulary ?? {
    search: 'Find entity or property',
    modesLabel: 'Properties to show',
    modes: MODES,
    mode: settings.properties,
    setMode: settings.setProperties,
    joins: true,
  };
  const mode = words.mode;
  const flow = useReactFlow();
  const container = useRef<HTMLDivElement>(null);
  const [connecting, setConnecting] = useState(false);
  const [search, setSearch] = useState('');
  /** The layout described under the layout choices: the one pointed at, or the current one. */
  const [layoutHint, setLayoutHint] = useState<LayoutKind | null>(null);
  const [focus, setFocus] = useState<Focus | null>(null);
  /** Cards dragged while focused; focus layouts are not saved. */
  const [focusDrags, setFocusDrags] = useState<Positions>({});
  const [nodes, setNodes, onNodesChange] = useNodesState<CanvasNode>([]);
  const fitted = useRef<string | null>(null);
  // Side by side, both canvases share one viewport, so focus (which moves cards) is off.
  const canFocus = !onViewport;
  // On touch screens cards stay where they are unless asked, so a swipe anywhere pans and a pinch zooms.
  const touch = useMedia(TOUCH);
  const [moveCards, setMoveCards] = useState(false);
  const inspectorDocks = useMedia(INSPECTOR_DOCKS);
  // A narrow diagram (a phone, a split) keeps its toolbar on one row: the search opens from a button.
  const narrow = useNarrow(container, 600);
  const [searchOpen, setSearchOpen] = useState(false);
  const zoomedOut = useStore((s) => zoomedOutAt(s.transform[2]));

  // Cascade paths: while on, the diagram shows what deleting one row of the selected entity does, as
  // one database does it (the one the model was made for, unless chosen). Nothing is hidden.
  const [deleting, setDeleting] = useState(false);
  const [databaseChoice, setDatabase] = useState<DeleteDatabase | null>(null);
  const guessedDatabase = useMemo(() => (model ? guessDatabase(model) : null), [model]);
  const database = databaseChoice ?? guessedDatabase ?? 'sqlServer';
  const selectedNode = selected.node ?? (selected.row ? selected.row.split('::')[0]! : null);
  /** The entity whose row is deleted: the selected card, kept while a line is selected (the list selects them). */
  const [deleteStart, setDeleteStart] = useState<string | null>(null);
  useEffect(() => {
    if (selectedNode) setDeleteStart(model?.entities[selectedNode] ? selectedNode : null);
    else if (!selected.edge) setDeleteStart(null);
  }, [selectedNode, selected.edge, model]);
  const impact = useMemo(
    () => (deleting && model && deleteStart && model.entities[deleteStart] ? deleteImpact(model, deleteStart, database) : null),
    [deleting, model, deleteStart, database],
  );
  /** Cascade paths lit up: pointed at (a card or an item in the list), or pinned by a click. */
  const [pointed, setPointed] = useState<number[] | null>(null);
  const [pinned, setPinned] = useState<number[] | null>(null);
  useEffect(() => {
    setPointed(null);
    setPinned(null);
  }, [impact]);
  const listTop = useListTop(container, deleting && !!model && !narrow);

  const kind = settings.layout;
  const view = useMemo<FocusGraph | null>(() => (focus ? focusGraph(graph, focus.id, settings.focusDepth, mode, measureText) : null), [graph, focus, settings.focusDepth, mode]);
  const baseGraph = view?.graph ?? graph;
  const deletion = useMemo(() => (impact ? deletionOverlay(baseGraph, impact) : null), [impact, baseGraph]);
  const shownGraph = deletion?.graph ?? baseGraph;
  const byId = useMemo(() => new Map(shownGraph.nodes.map((n) => [n.id, n])), [shownGraph]);

  // Focus is arranged with the chosen layout: its own arrangement for the four flow directions,
  // rings around the focused card for radial, and the layout engine (in the background) for the rest.
  const quick = useMemo(() => {
    if (!view || !focus) return null;
    const scale = spacingScale(settings.spacing, view.graph.nodes.length);
    if (isFlow(kind)) return focusArrangement(view, focus.id, kind, scale);
    const input = layoutInput(view.graph);
    if (kind === 'radial') return radialLayout(input.nodes, input.edges, focus.id, scale.layers);
    if (kind === 'grid') return gridLayout(input.nodes, scale.nodes);
    return null;
  }, [view, focus, kind, settings.spacing]);
  const [computed, setComputed] = useState<{ view: FocusGraph; kind: LayoutKind; spacing: Spacing; positions: Positions } | null>(null);
  useEffect(() => {
    if (!view || quick) return;
    let cancelled = false;
    const input = layoutInput(view.graph);
    const spacing = settings.spacing;
    void autoLayout(input.nodes, input.edges, kind, spacing).then((p) => {
      if (!cancelled) setComputed({ view, kind, spacing, positions: p });
    });
    return () => {
      cancelled = true;
    };
  }, [view, quick, kind, settings.spacing]);
  const focusPositions = quick ?? (computed && computed.view === view && computed.kind === kind && computed.spacing === settings.spacing ? computed.positions : null);
  // Null while a focus arrangement is being computed: cards stay where they are until it is ready.
  const target = useMemo<Positions | null>(() => (view ? (focusPositions ? { ...focusPositions, ...focusDrags } : null) : positions), [view, focusPositions, focusDrags, positions]);
  const { shown, gliding, settle } = useGlide(target, shownGraph.nodes.length > FRAME_BY_FRAME_CARDS);
  const settled = target ?? shown ?? positions;

  // The focused card left the model (another timeline step, a deleted entity): show everything.
  useEffect(() => {
    if (focus && !graph.nodes.some((n) => n.id === focus.id)) setFocus(null);
  }, [focus, graph]);
  useEffect(() => setFocusDrags({}), [focus?.id, settings.focusDepth, kind, settings.spacing]);
  const focused = !!focus;
  useEffect(() => onFocusChange?.(focused), [focused, onFocusChange]);

  const focusOn = useCallback(
    (id: string) => {
      if (!canFocus) return;
      setFocus((f) => (f?.id === id ? f : { id, trail: f ? [...f.trail, f.id] : [] }));
    },
    [canFocus],
  );
  const leaveFocus = useCallback(() => setFocus(null), []);
  const stepBack = useCallback(() => setFocus((f) => (f && f.trail.length ? { id: f.trail.at(-1)!, trail: f.trail.slice(0, -1) } : null)), []);

  /** Shows these bounds (diagram coordinates) at no more than `maxZoom`, below the toolbar and focus bar. */
  const fitTo = useCallback(
    (bounds: { x: number; y: number; width: number; height: number }, maxZoom: number, duration: number) => {
      const rect = container.current?.getBoundingClientRect();
      if (!rect || rect.width === 0) return;
      const toolbar = container.current?.querySelector('.react-flow__panel.top.left')?.getBoundingClientRect();
      const top = toolbar && toolbar.height > 0 ? Math.min(toolbar.bottom - rect.top, rect.height / 3) : 0;
      // The cascade paths list covers the right side of a wide diagram: the cards fit beside it.
      const list = container.current?.querySelector('.mv-delete-side > *')?.getBoundingClientRect();
      const right = list && list.width > 0 ? Math.min(rect.right - list.left, rect.width / 2) : 0;
      const vp = getViewportForBounds(bounds, rect.width - right, rect.height - top, 0.05, maxZoom, 0.12);
      void flow.setViewport({ ...vp, y: vp.y + top }, { duration });
    },
    [flow],
  );
  const boundsOf = useCallback(
    (ids: string[], at: Positions) => {
      const boxes = ids.flatMap((id) => {
        const p = at[id];
        const n = byId.get(id);
        return p && n ? [{ x: p.x, y: p.y, r: p.x + n.width, b: p.y + n.height }] : [];
      });
      if (!boxes.length) return null;
      const x = Math.min(...boxes.map((b) => b.x));
      const y = Math.min(...boxes.map((b) => b.y));
      return { x, y, width: Math.max(...boxes.map((b) => b.r)) - x, height: Math.max(...boxes.map((b) => b.b)) - y };
    },
    [byId],
  );

  useEffect(() => {
    if (!apiRef) return;
    apiRef.current = {
      visible: () => {
        const rect = container.current?.getBoundingClientRect();
        if (!rect) return { x: 0, y: 0, width: 0, height: 0 };
        const a = flow.screenToFlowPosition({ x: rect.left, y: rect.top });
        const b = flow.screenToFlowPosition({ x: rect.right, y: rect.bottom });
        return { x: a.x, y: a.y, width: b.x - a.x, height: b.y - a.y };
      },
      fitAll: () => void flow.fitView({ padding: 0.18, duration: 300, maxZoom: 1.1 }),
    };
    return () => {
      apiRef.current = null;
    };
  }, [apiRef, flow]);

  const matches = useMemo(() => {
    if (!search.trim()) return null;
    return new Set(shownGraph.nodes.filter((n) => matchesSearch(n, search.trim())).map((n) => n.id));
  }, [shownGraph, search]);
  const lit = useMemo(() => {
    const steps = pointed ?? pinned;
    return deletion && steps ? pathsOf(deletion, steps) : null;
  }, [deletion, pointed, pinned]);
  const faded = useMemo(() => {
    if (matches) return matches;
    if (lit) return lit.nodes;
    if (!dimUnchanged) return null;
    const changed = new Set(shownGraph.nodes.filter((n) => n.diff || n.lines.some((l) => (l.kind === 'row' ? l.row.diff : l.section.diff))).map((n) => n.id));
    for (const e of shownGraph.edges) {
      if (!e.diff) continue;
      changed.add(e.source);
      changed.add(e.target);
    }
    return changed;
  }, [matches, lit, dimUnchanged, shownGraph]);
  /** Cascade paths: what the delete does not reach is drawn softer, still readable. */
  const emphasis = !matches && !lit && deletion ? deletion.nodes : null;

  // Frames around the groups of a grouped layout, following the cards (each frame of a glide, so
  // the groups are worked out once).
  const grouping = useMemo(() => {
    if (!isGrouped(kind)) return null;
    const input = layoutInput(shownGraph);
    const groups = assignGroups(input.nodes, input.edges, kind);
    return { groups, labels: groupLabels([...new Set(groups.values())]) };
  }, [kind, shownGraph]);
  const frames = useMemo<FrameNodeType[]>(() => {
    if (!shown || !grouping) return [];
    const { groups, labels } = grouping;
    const boxes = new Map<string, { x: number; y: number; r: number; b: number; count: number }>();
    for (const n of shownGraph.nodes) {
      const p = shown[n.id];
      const g = groups.get(n.id);
      if (!p || !g) continue;
      const box = boxes.get(g) ?? { x: Infinity, y: Infinity, r: -Infinity, b: -Infinity, count: 0 };
      boxes.set(g, { x: Math.min(box.x, p.x), y: Math.min(box.y, p.y), r: Math.max(box.r, p.x + n.width), b: Math.max(box.b, p.y + n.height), count: box.count + 1 });
    }
    return [...boxes].map(([g, b]) => ({
      id: `frame:${g}`,
      type: 'frame' as const,
      position: { x: b.x - 28, y: b.y - 56 },
      width: b.r - b.x + 56,
      height: b.b - b.y + 84,
      data: { label: labels.get(g) ?? g, count: b.count },
      draggable: false,
      selectable: false,
      focusable: false,
      zIndex: -1,
    }));
  }, [shown, grouping, shownGraph]);

  useEffect(() => {
    if (!shown) return;
    setNodes((before) => {
      // A card that shows the same thing keeps what React Flow measured. Without it React Flow
      // measures every card and its handles again, and drops every line until it has, each time
      // the cards move (every frame of a glide) or the selection changes.
      const old = new Map(before.map((n) => [n.id, n]));
      const cards: EntityNodeType[] = shownGraph.nodes.map((n) => {
        const was = old.get(n.id);
        return {
          id: n.id,
          type: 'entity',
          position: shown[n.id] ?? { x: 0, y: 0 },
          // A card new to a focus arrangement that is still being computed appears when it is ready.
          hidden: !shown[n.id],
          data: n,
          width: n.width,
          height: n.height,
          measured: was?.data === n ? was.measured : undefined,
          selected: selected.node === n.id,
          className: view && n.id === focus?.id ? 'mv-focused' : undefined,
        };
      });
      return [...frames, ...cards];
    });
  }, [shownGraph, shown, frames, selected.node, view, focus?.id, setNodes]);

  // A new arrangement is computed in the background; until it arrives, the old positions are stale.
  const stale = useRef<Positions | null>(null);
  const arrangement = `${settings.layout}|${settings.spacing}`;
  const layoutSeen = useRef(arrangement);
  useEffect(() => {
    if (layoutSeen.current === arrangement) return;
    layoutSeen.current = arrangement;
    stale.current = positions;
    // `positions` is what is shown when the arrangement changes, not a reason to run again.
  }, [arrangement]);

  // Fit once per fit key, and again when focus or the arrangement changes what is shown where.
  useEffect(() => {
    if (nodes.length === 0) return;
    const token = `${fitKey}|${mode}|${arrangement}|${focus ? `${focus.id}|${settings.focusDepth}` : ''}`;
    if (fitted.current === token) return;
    if (!focus && stale.current === positions) return;
    stale.current = null;
    const ids = shownGraph.nodes.map((n) => n.id);
    if (!target || !ids.every((id) => target[id])) return;
    const first = fitted.current === null;
    fitted.current = token;
    const bounds = boundsOf(ids, target);
    if (bounds) requestAnimationFrame(() => fitTo(bounds, focus ? 1 : 1.1, first ? 0 : 300));
  }, [nodes.length, fitKey, mode, arrangement, settings.focusDepth, focus, shownGraph, positions, target, boundsOf, fitTo]);

  // Each reveal request is handled once, when its card has a position.
  const revealed = useRef<number | null>(null);
  /** Cascade paths were asked for this entity: what its delete reaches comes into view once drawn. */
  const fitDeletion = useRef<string | null>(null);
  useEffect(() => {
    if (!reveal || revealed.current === reveal.nonce || !positions[reveal.id]) return;
    revealed.current = reveal.nonce;
    const deletes = !!reveal.deletion && !!model?.entities[reveal.id];
    if (deletes) {
      setDeleting(true);
      setDeleteStart(reveal.id);
    }
    // Focused, or asked to focus: the revealed entity becomes the focus.
    if (canFocus && (reveal.focus || focus)) {
      focusOn(reveal.id);
      return;
    }
    if (deletes) fitDeletion.current = reveal.id;
    else requestAnimationFrame(() => void flow.fitView({ nodes: [{ id: reveal.id }], padding: 0.8, duration: 300, maxZoom: 1.2 }));
  }, [reveal, positions, flow, canFocus, focus, focusOn, model]);

  /** Brings these cards into view when some are out of it or under the list, never zooming in. */
  const showCards = useCallback(
    (ids: string[]) => {
      const rect = container.current?.getBoundingClientRect();
      const list = container.current?.querySelector('.mv-delete-side > *')?.getBoundingClientRect();
      if (!rect) return;
      const a = flow.screenToFlowPosition({ x: rect.left, y: rect.top });
      const b = flow.screenToFlowPosition({ x: list && list.width > 0 ? list.left : rect.right, y: rect.bottom });
      const inView = ids.every((id) => {
        const p = settled[id];
        const n = byId.get(id);
        return !p || !n || (p.x >= a.x && p.y >= a.y && p.x + n.width <= b.x && p.y + n.height <= b.y);
      });
      const bounds = boundsOf(ids, settled);
      if (!inView && bounds) fitTo(bounds, flow.getViewport().zoom, 300);
    },
    [flow, settled, byId, boundsOf, fitTo],
  );
  // What a newly selected entity's delete reaches comes into view: fitted when asked for from
  // elsewhere (the inspector, the palette), otherwise only when some of it is out of view. Focus
  // fits its own cards.
  const shownStart = useRef<string | null>(null);
  useEffect(() => {
    const start = deletion?.impact.start ?? null;
    if (start === shownStart.current) return;
    shownStart.current = start;
    if (!deletion) return;
    const ids = [...deletion.nodes];
    if (fitDeletion.current === start) {
      fitDeletion.current = null;
      const bounds = boundsOf(ids, settled);
      if (bounds) requestAnimationFrame(() => fitTo(bounds, 1.1, 300));
    } else if (!focus) requestAnimationFrame(() => showCards(ids));
  }, [deletion, settled, focus, boundsOf, fitTo, showCards]);

  useEffect(() => {
    if (!viewport) return;
    const now = flow.getViewport();
    if (Math.abs(now.x - viewport.x) > 0.5 || Math.abs(now.y - viewport.y) > 0.5 || Math.abs(now.zoom - viewport.zoom) > 0.001) void flow.setViewport(viewport);
  }, [viewport, flow]);

  const edges = useMemo<DiagramEdgeType[]>(() => {
    return shownGraph.edges.map((e) => {
      // Sides come from where the cards end up, so lines do not flip while cards glide.
      const s = settled[e.source];
      const t = settled[e.target];
      const sw = byId.get(e.source)?.width ?? 0;
      const tw = byId.get(e.target)?.width ?? 0;
      const rightward = !s || !t ? true : s.x + sw / 2 <= t.x + tw / 2;
      const self = e.source === e.target;
      const dimmed = matches ? !(matches.has(e.source) && matches.has(e.target)) : lit ? !lit.edges.has(e.id) : !!dimUnchanged && !e.diff;
      const soft = !!emphasis && !deletion?.edges.has(e.id);
      return {
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: `${e.sourceRow ?? '__node'}:${self || rightward ? 'R' : 'L'}`,
        targetHandle: `${e.targetRow ?? '__node'}:${self ? 'R' : rightward ? 'L' : 'R'}`,
        type: 'relationship' as const,
        data: { edge: e, dimmed, soft },
        selected: selected.edge === e.id,
        zIndex: e.diff === 'removed' ? -1 : 0,
      };
    });
  }, [shownGraph, byId, settled, selected.edge, matches, lit, dimUnchanged, emphasis, deletion]);

  const onNodeDragStop = useCallback<OnNodeDrag<CanvasNode>>(() => {
    // Every card's position (a drag can move a whole selection).
    const update: Positions = {};
    for (const n of flow.getNodes()) if (n.type === 'entity') update[n.id] = { x: n.position.x, y: n.position.y };
    settle(update);
    if (view) setFocusDrags((d) => ({ ...d, ...update }));
    else onMoved(update);
  }, [flow, onMoved, settle, view]);

  // One function for the life of the canvas, so selecting a row only redraws the cards it changes.
  const rowTarget = useRef({ byId, onSelect });
  useLayoutEffect(() => {
    rowTarget.current = { byId, onSelect };
  });
  const selectRow = useCallback((nodeId: string, row: RowData) => {
    const node = rowTarget.current.byId.get(nodeId);
    if (node) rowTarget.current.onSelect({ kind: 'row', node, row });
  }, []);

  const interaction = useMemo<DiagramInteraction>(
    () => ({ selectedRow: selected.row, matches: faded, emphasis, selectRow, editable: !!onConnect, connecting, zoomedOut }),
    [selected.row, faded, emphasis, selectRow, onConnect, connecting, zoomedOut],
  );

  const connect = useCallback(
    (c: Connection) => {
      const from = byId.get(c.source);
      const to = byId.get(c.target);
      if (from && to && onConnect) onConnect(from, to);
    },
    [byId, onConnect],
  );

  const layout = LAYOUTS.find((l) => l.id === settings.layout) ?? LAYOUTS[0]!;
  const focusedNode = focus ? graph.nodes.find((n) => n.id === focus.id) : undefined;
  const related = view ? view.graph.nodes.length - 1 : 0;
  /** Entities with no path to the focused one; focus never shows them. */
  const apart = view ? graph.nodes.length - view.graph.nodes.length : 0;

  const deletePanel = (folded: boolean) => (
    <DeletePanel
      key={`${impact?.start ?? ''}|${folded}`}
      impact={impact}
      database={database}
      guessed={guessedDatabase}
      onDatabase={setDatabase}
      onClose={() => setDeleting(false)}
      folded={folded}
      className={folded ? 'max-h-[55vh]' : 'max-h-full'}
      onPoint={setPointed}
      pinned={pinned}
      onPin={setPinned}
      onPick={(steps, relationship) => {
        // Selecting a card would make it the one deleted; a relationship shows in the inspector.
        if (deletion) showCards([...pathsOf(deletion, steps).nodes].filter((id) => byId.has(id)));
        const edge = relationship === null ? undefined : shownGraph.edges.find((e) => e.relationship === relationship);
        if (edge) onSelect({ kind: 'edge', edge });
      }}
    />
  );
  const showDeletion = deleting && !!model;

  /** What is selected, named, when the inspector is not beside the diagram (narrow screens). */
  const selectedName = useMemo(() => {
    if (inspectorDocks || bare) return null;
    if (selected.row) {
      const [nodeId, rowId] = selected.row.split('::') as [string, string];
      const n = byId.get(nodeId);
      return n ? `${n.label}.${rowId}` : null;
    }
    if (selected.node) return byId.get(selected.node)?.label ?? null;
    const edge = selected.edge ? shownGraph.edges.find((e) => e.id === selected.edge) : undefined;
    return edge ? `${byId.get(edge.source)?.label ?? edge.source} → ${byId.get(edge.target)?.label ?? edge.target}` : null;
  }, [inspectorDocks, bare, selected, byId, shownGraph]);

  return (
    <DiagramContext.Provider value={interaction}>
      <div
        ref={container}
        className={cn('@container h-full w-full', connecting && 'mv-connecting', view && 'mv-focus-mode', gliding && 'mv-gliding')}
        data-testid="diagram"
        onKeyDown={(e) => {
          if (e.key === 'Escape' && focus && !(e.target instanceof HTMLInputElement)) leaveFocus();
        }}
      >
        <DiagramMarkers />
        <ReactFlow<CanvasNode, DiagramEdgeType>
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodesChange={onNodesChange}
          onNodeDragStop={onNodeDragStop}
          onNodeClick={(_, node) => node.type === 'entity' && onSelect({ kind: 'node', node: node.data })}
          onNodeDoubleClick={(_, node) => node.type === 'entity' && node.data.diff !== 'removed' && focusOn(node.id)}
          onEdgeClick={(_, edge) => edge.data && onSelect({ kind: 'edge', edge: edge.data.edge })}
          onPaneClick={() => {
            onSelect(null);
            setPinned(null);
          }}
          // Cascade paths: the paths to the card pointed at light up.
          onNodeMouseEnter={(_, node) => {
            if (!deletion || node.id === deletion.impact.start) return;
            const steps = deletion.impact.steps.flatMap((s, i) => (s.entity === node.id ? [i] : []));
            if (steps.length) setPointed(steps);
          }}
          onNodeMouseLeave={() => deletion && setPointed(null)}
          onMove={(event, vp) => {
            if (event) onViewport?.(vp);
          }}
          zoomOnDoubleClick={false}
          nodesDraggable={!touch || moveCards}
          connectionMode={ConnectionMode.Loose}
          nodesConnectable={!!onConnect}
          onConnect={connect}
          onConnectStart={() => setConnecting(true)}
          onConnectEnd={() => setConnecting(false)}
          connectionRadius={36}
          connectionLineStyle={{ stroke: 'var(--accent)', strokeWidth: 1.6, strokeDasharray: '5 4' }}
          elementsSelectable
          minZoom={0.05}
          maxZoom={2.5}
          // Zoomed in on a large model, only the cards in view are drawn. Zoomed out, cards are light
          // (bars for rows) and all of them stay: drawing them as they come into view while cards
          // glide to a new layout or the view pans costs far more than keeping them.
          onlyRenderVisibleElements={shownGraph.nodes.length > 120 && !zoomedOut}
          attributionPosition="bottom-center"
          deleteKeyCode={null}
        >
          <Background variant={BackgroundVariant.Dots} gap={22} size={1.3} color="var(--canvas-dot)" />
          {/* Touch screens zoom with a pinch, so only "fit" and the switch for moving cards. */}
          <Controls showInteractive={false} showZoom={!touch} position="bottom-left">
            {touch && (
              <ControlButton
                onClick={() => setMoveCards(!moveCards)}
                title={moveCards ? 'Moving cards: drag a card to move it. Tap to pan with a swipe again.' : 'Move cards (a swipe pans the diagram until then)'}
                aria-label={moveCards ? 'Stop moving cards' : 'Move cards'}
                aria-pressed={moveCards}
                className={cn(moveCards && 'mv-control-on')}
              >
                {moveCards ? <Move /> : <Hand />}
              </ControlButton>
            )}
          </Controls>
          {settings.minimap && (
            <MiniMap
              pannable
              zoomable
              // Narrow or short (a phone, upright or on its side): no room for it.
              className="@max-[40rem]:hidden [@media(max-height:30rem)]:hidden"
              position="bottom-right"
              style={{ width: 168, height: 112 }}
              nodeColor={(n) => {
                if (n.type === 'frame') return 'transparent';
                const data = n.data as EntityNodeData;
                const effect = data.deleteStart ? 'delete' : data.effects?.[0];
                if (effect) return `var(--effect-${effect})`;
                const diff = data.diff;
                return diff === 'added' ? 'var(--added)' : diff === 'removed' ? 'var(--removed)' : diff === 'changed' ? 'var(--changed)' : 'var(--node-header)';
              }}
              nodeStrokeColor={(n) => (n.type === 'frame' ? 'var(--border-strong)' : 'var(--node-border)')}
              maskColor="rgb(0 0 0 / 0.2)"
            />
          )}
          <Panel position="top-left" className={cn('!m-3 flex max-w-[calc(100%-1.5rem)] flex-col items-start gap-1.5', bare && 'hidden')}>
            <div className="flex flex-wrap items-center gap-1 rounded-lg border border-border bg-panel-2/95 p-1 shadow-sm backdrop-blur">
              {narrow && !searchOpen && !search ? (
                <ToolButton label={words.search} onClick={() => setSearchOpen(true)}>
                  <Search size={15} />
                </ToolButton>
              ) : (
                <label className="flex items-center gap-1.5 rounded-md px-2 text-muted focus-within:text-text">
                  <Search size={14} aria-hidden />
                  <input
                    value={search}
                    autoFocus={narrow}
                    onChange={(e) => setSearch(e.target.value)}
                    onBlur={() => setSearchOpen(false)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && matches?.size) void flow.fitView({ nodes: [...matches].map((id) => ({ id })), padding: 0.3, duration: 300 });
                      if (e.key === 'Escape') setSearch('');
                    }}
                    placeholder={words.search}
                    aria-label={words.search}
                    className="h-7 w-44 bg-transparent text-[12.5px] text-text outline-none placeholder:text-faint @max-[44rem]:w-24 pointer-coarse:h-9 @max-[37.5rem]:w-32"
                  />
                  {matches && <span className="text-[11px] text-faint">{matches.size}</span>}
                  {narrow && search && (
                    <button type="button" aria-label="Clear the search" onClick={() => setSearch('')} className="-mr-1 flex h-6 w-6 items-center justify-center rounded text-faint hover:text-text">
                      <X size={13} />
                    </button>
                  )}
                </label>
              )}
              {!narrow && (
                <>
                  <Divider />
                  <div className="flex rounded-md bg-panel p-0.5" role="radiogroup" aria-label={words.modesLabel}>
                    {words.modes.map((m) => (
                      <button
                        key={m.value}
                        role="radio"
                        aria-checked={mode === m.value}
                        title={'title' in m ? m.title : undefined}
                        onClick={() => words.setMode(m.value)}
                        className={cn('h-6 rounded px-2 text-[12px] pointer-coarse:h-8', mode === m.value ? 'bg-active text-text' : 'text-muted hover:text-text')}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
              <Divider />
              {words.joins && (
                <ToolButton
                  label={settings.collapseJoins ? 'Show join tables' : 'Draw join tables as many-to-many links'}
                  active={settings.collapseJoins}
                  onClick={settings.toggleCollapseJoins}
                >
                  <Waypoints size={15} />
                </ToolButton>
              )}
              {canFocus && (
                <ToolButton
                  label={focus ? 'Show everything again (Esc)' : selectedNode ? `Focus on the selected ${vocabulary ? 'card' : 'entity'} (or double-click a card)` : 'Focus: double-click a card, or select one first'}
                  active={!!focus}
                  disabled={!focus && !selectedNode}
                  onClick={() => (focus ? leaveFocus() : selectedNode && focusOn(selectedNode))}
                >
                  <Crosshair size={15} />
                </ToolButton>
              )}
              {model && (
                <ToolButton label="Cascade paths: what deleting a row of the selected table does" active={deleting} onClick={() => setDeleting(!deleting)}>
                  <CascadeIcon size={15} />
                </ToolButton>
              )}
              {tools}
              <Divider />
              {/* Not modal: a modal menu locks the page's scrolling, which restyles and lays out the
                  whole page as it opens and closes (a tenth of a second each on a large model). */}
              <Menu.Root modal={false} onOpenChange={() => setLayoutHint(null)}>
                <Menu.Trigger asChild>
                  <button
                    type="button"
                    title={`Layout: ${layout.label}`}
                    aria-label={`Layout: ${layout.label}`}
                    className="flex h-7 items-center gap-1.5 rounded-md px-1.5 text-[12px] text-muted hover:bg-hover hover:text-text data-[state=open]:bg-active data-[state=open]:text-text pointer-coarse:h-9"
                  >
                    {LAYOUT_ICONS[layout.id]}
                    <span className="@max-[52rem]:hidden">{layout.label}</span>
                    <ChevronDown size={12} className="text-faint" />
                  </button>
                </Menu.Trigger>
                <Menu.Portal>
                  <Menu.Content
                    align="start"
                    sideOffset={6}
                    collisionPadding={8}
                    className="mv-menu z-50 max-h-[var(--radix-dropdown-menu-content-available-height)] w-[316px] overflow-y-auto rounded-lg border border-border-strong bg-panel-2 p-1.5 shadow-[var(--shadow-pop)]"
                  >
                    {narrow && (
                      <>
                        {/* Beside the search on wider diagrams. */}
                        <Menu.Label className={MENU_LABEL}>{words.modesLabel}</Menu.Label>
                        <Menu.RadioGroup value={mode} onValueChange={(v) => words.setMode(v as PropertyMode)} className="grid grid-cols-3 gap-1">
                          {words.modes.map((m) => (
                            <Menu.RadioItem key={m.value} value={m.value} className={SEGMENT}>
                              {m.label}
                            </Menu.RadioItem>
                          ))}
                        </Menu.RadioGroup>
                        <Menu.Separator className="my-1.5 h-px bg-border" />
                      </>
                    )}
                    <Menu.Label className={MENU_LABEL}>Arrange cards</Menu.Label>
                    <Menu.RadioGroup value={settings.layout} onValueChange={(v) => settings.setLayout(v as LayoutKind)} className="grid grid-cols-3 gap-1">
                      {LAYOUTS.map((l) => (
                        <Menu.RadioItem
                          key={l.id}
                          value={l.id}
                          title={l.description}
                          onFocus={() => setLayoutHint(l.id)}
                          className="flex cursor-default flex-col items-center gap-1 rounded-md px-1 pt-2 pb-1.5 text-center text-[11.5px] leading-tight text-muted outline-none data-[highlighted]:bg-active data-[highlighted]:text-text data-[state=checked]:bg-accent-soft data-[state=checked]:text-accent"
                        >
                          {LAYOUT_ICONS[l.id]}
                          {l.label}
                        </Menu.RadioItem>
                      ))}
                    </Menu.RadioGroup>
                    <p className="min-h-[2.6em] px-1.5 pt-1.5 text-[11.5px] leading-snug text-faint" aria-hidden>
                      {(LAYOUTS.find((l) => l.id === layoutHint) ?? layout).description}
                    </p>
                    <Menu.Separator className="my-1.5 h-px bg-border" />
                    <Menu.Label className={MENU_LABEL}>Spacing</Menu.Label>
                    <Menu.RadioGroup value={settings.spacing} onValueChange={(v) => settings.setSpacing(v as Spacing)} className="grid grid-cols-3 gap-1">
                      {SPACINGS.map((s) => (
                        <Menu.RadioItem key={s.id} value={s.id} title={s.description} className={SEGMENT}>
                          {SPACING_ICONS[s.id]}
                          {s.label}
                        </Menu.RadioItem>
                      ))}
                    </Menu.RadioGroup>
                    <Menu.Label className={cn(MENU_LABEL, 'pt-2.5')}>Lines</Menu.Label>
                    <Menu.RadioGroup value={settings.edgeStyle} onValueChange={(v) => settings.setEdgeStyle(v as EdgeStyle)} className="grid grid-cols-2 gap-1">
                      {EDGE_STYLES.map((s) => (
                        <Menu.RadioItem key={s.id} value={s.id} className={SEGMENT}>
                          {s.icon}
                          {s.label}
                        </Menu.RadioItem>
                      ))}
                    </Menu.RadioGroup>
                    <Menu.Separator className="my-1 h-px bg-border" />
                    <Menu.Item
                      onSelect={() => {
                        fitted.current = null;
                        // In focus the arrangement is computed again (forgetting moved cards); otherwise the saved one.
                        if (focus) setFocusDrags({});
                        else {
                          stale.current = positions;
                          onRelayout();
                        }
                      }}
                      className="flex cursor-default items-center gap-2.5 rounded-md px-2 py-1.5 text-[12.5px] text-text outline-none data-[highlighted]:bg-active"
                    >
                      <RotateCcw size={14} className="text-muted" /> Arrange again
                      <span className="ml-auto text-[11.5px] text-faint">forgets moved cards</span>
                    </Menu.Item>
                  </Menu.Content>
                </Menu.Portal>
              </Menu.Root>
              <ToolButton
                label="Fit to screen"
                onClick={() => {
                  const bounds = boundsOf(
                    shownGraph.nodes.map((n) => n.id),
                    settled,
                  );
                  if (bounds) fitTo(bounds, 2.5, 250);
                }}
              >
                <Maximize size={15} />
              </ToolButton>
            </div>
            {focus && focusedNode && (
              <div className="flex flex-wrap items-center gap-1 rounded-lg border border-accent/40 bg-panel-2/95 p-1 text-[12px] shadow-sm backdrop-blur" role="group" aria-label="Focus">
                {focus.trail.length > 0 && (
                  <ToolButton label={`Back to ${graph.nodes.find((n) => n.id === focus.trail.at(-1))?.label ?? focus.trail.at(-1)}`} onClick={stepBack}>
                    <ArrowLeft size={14} />
                  </ToolButton>
                )}
                <span className="flex items-center gap-1.5 px-1.5 text-muted">
                  <Crosshair size={13} className="text-accent" />
                  Focus on <strong className="font-semibold text-text">{focusedNode.label}</strong>
                  <span className="text-faint">
                    · {related === 0 ? 'nothing related' : `${related} related`}
                    {settings.focusDepth === 'all' && apart > 0 && ` · ${apart} not connected`}
                  </span>
                </span>
                <span className="pl-1 text-faint" aria-hidden>
                  Steps
                </span>
                <div className="flex rounded-md bg-panel p-0.5" role="radiogroup" aria-label="Steps around the focus">
                  {FOCUS_DEPTHS.map((d) => (
                    <button
                      key={d}
                      role="radio"
                      aria-checked={settings.focusDepth === d}
                      title={d === 'all' ? 'Everything connected to it, however far' : `${d} step${d > 1 ? 's' : ''} out`}
                      onClick={() => settings.setFocusDepth(d)}
                      className={cn('h-6 rounded px-2 text-[12px] pointer-coarse:h-8 pointer-coarse:px-2.5', settings.focusDepth === d ? 'bg-active text-text' : 'text-muted hover:text-text')}
                    >
                      {d === 'all' ? 'All' : d}
                    </button>
                  ))}
                </div>
                <ToolButton label="Show everything again (Esc)" onClick={leaveFocus}>
                  <X size={14} />
                </ToolButton>
              </div>
            )}
            {showDeletion && narrow && deletePanel(true)}
          </Panel>
          {showDeletion && !narrow && (
            // Stretched down to the minimap, so a long list scrolls; only the list takes the pointer.
            <Panel
              position="top-right"
              className="mv-delete-side pointer-events-none !m-3 flex !bottom-[140px] flex-col items-end [&>*]:pointer-events-auto"
              style={listTop === null ? undefined : { top: listTop - 12 }}
            >
              {deletePanel(false)}
            </Panel>
          )}
          <Panel position="bottom-left" className="!mb-[15px] !ml-[54px] max-w-[calc(100%-70px)] pointer-coarse:!ml-[62px]">
            <div className="rounded-md border border-border bg-panel-2/90 px-2.5 py-1 text-[11.5px] text-muted backdrop-blur" data-testid="diagram-status">
              {status}
            </div>
          </Panel>
          {selectedName && (
            <Panel position="bottom-center" className="!mb-16">
              <button
                type="button"
                onClick={() => showSidebar('inspector')}
                className="flex max-w-[min(300px,70vw)] items-center gap-1.5 rounded-full border border-border-strong bg-panel-2/95 py-1.5 pr-2.5 pl-3.5 text-[12.5px] shadow-[var(--shadow-pop)] backdrop-blur pointer-coarse:py-2"
                data-testid="selection-details"
              >
                <span className="truncate font-medium text-text">{selectedName}</span>
                <span className="flex flex-none items-center text-accent">
                  Details <ChevronRight size={14} />
                </span>
              </button>
            </Panel>
          )}
        </ReactFlow>
      </div>
    </DiagramContext.Provider>
  );
}

export function Divider() {
  return <span className="mx-0.5 h-5 w-px bg-border" aria-hidden />;
}

export function ToolButton(props: { label: string; active?: boolean; disabled?: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      title={props.label}
      aria-label={props.label}
      aria-pressed={props.active}
      disabled={props.disabled}
      onClick={props.onClick}
      className={cn(
        'flex h-7 min-w-7 items-center justify-center gap-0.5 rounded-md px-1.5 disabled:opacity-35 pointer-coarse:h-9 pointer-coarse:min-w-9',
        props.active ? 'bg-accent-soft text-accent' : 'text-muted hover:bg-hover hover:text-text',
      )}
    >
      {props.children}
    </button>
  );
}
